import api from "./api";

const BASE_URL = process.env.REACT_APP_PUSH_API_BASE || "/push";

/* ------------------------------------------------------------------ *
 * HELPERS
 * ------------------------------------------------------------------ */
export const transformJob = (backendJob) => {
  if (!backendJob) return null;

  return {
    ...backendJob,
    id: backendJob._id || backendJob.id,

    /* content ------------------------------------------------------ */
    templateKey: backendJob.templateKey,
    templateVersion: backendJob.templateVersion,
    hasCustomContent: !backendJob.templateKey && backendJob.rawTitle,

    /* status / progress ------------------------------------------- */
    status: backendJob.status,
    canBeCancelled:
      backendJob.canBeCancelled ||
      ["QUEUED", "SENDING"].includes(backendJob.status),
    isComplete:
      backendJob.isComplete ||
      ["SENT", "FAILED", "CANCELLED"].includes(backendJob.status),
    progress:
      backendJob.progress ??
      (backendJob.stats?.total
        ? Math.round((backendJob.stats.sent / backendJob.stats.total) * 100)
        : 0),

    /* stats -------------------------------------------------------- */
    stats: backendJob.stats || {
      total: 0,
      sent: 0,
      failed: 0,
      delivered: 0,
      opened: 0,
      clicked: 0, // <-- new metric
    },

    /* audience ----------------------------------------------------- */
    audience: {
      total: backendJob.stats?.total || backendJob.customerIds?.length || 0,
      channels: backendJob.channels || [],
      platforms: (backendJob.channels || []).map((ch) => {
        switch (ch) {
          case "FCM":
            return "Android";
          case "APNS":
            return "iOS";
          case "WEB":
            return "Web";
          case "WHATSAPP":
            return "WhatsApp";
          default:
            return ch;
        }
      }),
    },

    /* misc --------------------------------------------------------- */
    isPromotional: backendJob.isPromotional || false,
    createdBy: backendJob.createdBy,
    createdAt: backendJob.createdAt,
    completedAt: backendJob.completedAt,
    scheduleAt: backendJob.scheduleAt,
  };
};

/* ------------------------------------------------------------------ *
 * API WRAPPERS
 * ------------------------------------------------------------------ */
// 1. create campaign
export const createCampaign = (payload) =>
  api.post(`${BASE_URL}/notifications/send`, payload);

// 2. list jobs  (backend now returns { data: [...], pagination: {...} })
export const getJobs = async (params = {}) => {
  const resp = await api.get(`${BASE_URL}/notifications/jobs`, { params });

  if (resp.data.ok) {
    const jobs = (resp.data.data || resp.data.jobs || []).map(transformJob);

    return {
      ok: true,
      jobs,
      pagination: resp.data.pagination || {},
    };
  }

  return { ok: false, error: resp.data.error || "Failed to fetch jobs" };
};

// 3. job details (unchanged)
export const getJobDetails = async (jobId) => {
  const resp = await api.get(`${BASE_URL}/notifications/jobs/${jobId}`);

  return resp.data.ok
    ? { ...resp.data, job: transformJob(resp.data.job) }
    : resp.data;
};

// 4. audits   (backend now returns { data: [...], nextCursor })
export const getJobAudits = async (jobId, params) =>
  api.get(`${BASE_URL}/notifications/jobs/${jobId}/audits`, { params });

// 5. cancel
export const cancelJob = async (jobId) => {
  const resp = await api.post(`${BASE_URL}/notifications/jobs/${jobId}/cancel`);

  if (resp.data.ok) {
    // job payload is now nested under data.job
    const job = transformJob(resp.data.data?.job || resp.data.job);
    return { ok: true, job };
  }

  return resp.data;
};

// 6. retry   (unchanged)
export const retryJob = (jobId) =>
  api.post(`${BASE_URL}/notifications/jobs/${jobId}/retry`);

// 7. aggregated stats   (endpoint unchanged)
export const getJobStats = (period = "day") =>
  api.get(`${BASE_URL}/notifications/stats`, { params: { period } });

/* ------------------------------------------------------------------ *
 * FORMATTERS (no changes, kept for completeness)
 * ------------------------------------------------------------------ */
export const formatJobStatus = (status) => {
  const statusMap = {
    QUEUED: { label: "Queued", color: "yellow" },
    SENDING: { label: "Sending", color: "blue" },
    SENT: { label: "Sent", color: "green" },
    FAILED: { label: "Failed", color: "red" },
    CANCELLED: { label: "Cancelled", color: "gray" },
  };
  return statusMap[status] || { label: status, color: "gray" };
};

export const formatChannel = (channel) => {
  const channelMap = {
    FCM: "Android (FCM)",
    APNS: "iOS (APNS)",
    WEB: "Web Push",
    WHATSAPP: "WhatsApp",
  };
  return channelMap[channel] || channel;
};

/* ------------------------------------------------------------------ *
 * CUSTOMER & SEGMENT ENDPOINT HELPERS
 * ------------------------------------------------------------------ */

/**
 * Fetch user IDs that belong to one or more predefined/custom segments.
 * @param {object} body { segments: string[], testDevices?: string[] }
 */
export const getCustomersBySegments = (body) =>
  api.post(`${BASE_URL}/customers/by-segments`, body);

/**
 * Quick counts for each segment type so the UI can display “~12 k users”.
 * @param {string[]=} segmentList  optional comma-separated list
 */
export const getSegmentStats = (segmentList) =>
  api.get(`${BASE_URL}/customers/segment-stats`, {
    params: { segments: segmentList?.join(",") },
  });

/**
 * Verify arbitrary customer IDs before sending a test / custom blast.
 * @param {string[]} customerIds
 */
export const validateCustomerIds = (customerIds) =>
  api.post(`${BASE_URL}/customers/validate-ids`, { customerIds });

/**
 * Get channel status (enabled/disabled) for the customer
 * @returns {Promise} API response with enabled/disabled status for each channel
 */
export const getChannelStatus = () =>
  api.get(`${BASE_URL}/notifications/channels/status`);
